import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import apiService from '../services/api';

const PatientDetails = React.memo(({ patient, onBackToList, onViewChat, onViewSessions, token }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [importantMessages, setImportantMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMessages, setExpandedMessages] = useState({});

  // Toggle message expansion
  const toggleMessageExpanded = (messageId) => {
    setExpandedMessages(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  // Load patient stats and important messages
  useEffect(() => {
    if (token && patient?.id) {
      loadPatientData();
    }
  }, [token, patient?.id]);

  const loadPatientData = async () => {
    setLoading(true);
    try {
      // Load stats and important messages in parallel
      const [statsData, messagesData] = await Promise.all([
        apiService.getChatStats(token, patient.id),
        apiService.getImportantMessages(token, patient.id)
      ]);
      
      setStats(statsData);
      setImportantMessages(messagesData.messages || []);
    } catch (error) {
      console.error('Error loading patient data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!patient) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <Icon name="user-round" size={48} className="text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Klient hittades inte</h3>
          <p className="text-sm text-gray-500 mb-4">Den begärda klienten kunde inte hittas.</p>
          <button
            onClick={() => navigate('/therapist')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <Icon name="arrow-left" size={16} className="mr-2" />
            Tillbaka till klientlista
          </button>
        </div>
      </div>
    );
  }

  const getPatientInitials = (patient) => {
    if (!patient.name) return 'K';
    const names = patient.name.split(' ');
    return names.map(name => name.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatMessageDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE', { 
      day: 'numeric', 
      month: 'long' 
    }) + ', kl. ' + date.toLocaleTimeString('sv-SE', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getMessageTypeConfig = (messageType, importance) => {
    const configs = {
      'crisis': { 
        emoji: '🔴', 
        label: 'Kris', 
        color: 'text-red-600',
        bgColor: 'bg-red-50 border-red-200'
      },
      'breakthrough': { 
        emoji: '🎉', 
        label: 'Genombrott', 
        color: 'text-green-600',
        bgColor: 'bg-green-50 border-green-200'
      },
      'reflection': { 
        emoji: '🤔', 
        label: 'Reflektion', 
        color: 'text-blue-600',
        bgColor: 'bg-blue-50 border-blue-200'
      }
    };

    // Default for high importance messages without special type
    if (!configs[messageType] && importance >= 4) {
      return {
        emoji: importance === 5 ? '🔴' : '🟡',
        label: `Viktigt (${importance}⭐)`,
        color: importance === 5 ? 'text-red-600' : 'text-yellow-600',
        bgColor: importance === 5 ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
      };
    }

    return configs[messageType] || null;
  };

  const handleViewChat = () => {
    navigate(`/therapist/client/${patient.id}/chat`);
  };

  const handleViewAnamnesis = () => {
    navigate(`/therapist/client/${patient.id}/anamnesis`);
  };

  const handleViewSessions = () => {
    navigate(`/therapist/client/${patient.id}/sessions`);
  };

  return (
    <div className="max-w-4xl mx-auto">
      

      {/* Breadcrumb */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <button
              onClick={() => navigate('/therapist')}
              className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600"
            >
              <Icon name="users" size={16} className="mr-2" />
              Klienter
            </button>
          </li>
          <li>
            <div className="flex items-center">
              <Icon name="chevron-right" size={16} className="text-gray-400" />
              <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">{patient.name}</span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Patient Details Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-semibold text-xl">
                {getPatientInitials(patient)}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{patient.name}</h2>
                {/* <p className="text-xs text-gray-500 mt-1">
                  Klient-ID: {patient.id}
                </p> */}
              </div>
            </div>
            
            
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Kontakt</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Icon name="mail" size={16} className="text-gray-400 mr-3" />
                  <span className="text-sm text-gray-700">{patient.email}</span>
                </div>
                {patient.phone && (
                  <div className="flex items-center">
                    <Icon name="phone" size={16} className="text-gray-400 mr-3" />
                    <span className="text-sm text-gray-700">{patient.phone}</span>
                  </div>
                )}
                <div className="flex items-center">
                  <Icon name="calendar" size={16} className="text-gray-400 mr-3" />
                  <span className="text-sm text-gray-700">
                    Ansluten: {formatDate(patient.assigned_date)}
                  </span>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Övrigt</h3>
              <div className="space-y-3">
                {patient.date_of_birth && (
                  <div className="flex items-center">
                    <Icon name="calendar" size={16} className="text-gray-400 mr-3" />
                    <span className="text-sm text-gray-700">
                      Födelsedatum: {formatDate(patient.date_of_birth)}
                    </span>
                  </div>
                )}
                <div className="flex items-center">
                  <Icon name="id-card" size={16} className="text-gray-400 mr-3" />
                  <span className="text-sm text-gray-700">
                    Id: {patient.id}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Action Buttons */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <button 
                onClick={handleViewChat}
                className="flex-1 inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                <Icon name="message-circle" size={16} className="mr-2" />
                Visa AI-chat
              </button>
              <button 
                onClick={handleViewAnamnesis}
                className="flex-1 inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 transition-colors"
              >
                <Icon name="brain" size={16} className="mr-2" />
                Visa anamnesis
              </button>
              <button 
                onClick={handleViewSessions}
                className="flex-1 inline-flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <Icon name="file-text" size={16} className="mr-2" />
                Visa sessioner
              </button>
            </div>
          </div>

          {/* Statistics Overview */}
          <div className="mt-8 bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Klientöversikt</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                <Icon name="message-circle-heart" size={24} className="mx-auto text-blue-500 mb-2" />
                <div className="text-2xl font-bold text-gray-900">
                  {loading ? '-' : stats?.total_messages || 0}
                </div>
                <div className="text-sm text-gray-600">AI-chattar</div>
              </div>
              <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                <Icon name="file-text" size={24} className="mx-auto text-green-500 mb-2" />
                <div className="text-2xl font-bold text-gray-900">-</div>
                <div className="text-sm text-gray-600">Sessioner</div>
              </div>
              <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                <Icon name="calendar" size={24} className="mx-auto text-purple-500 mb-2" />
                <div className="text-2xl font-bold text-gray-900">
                  {patient.assigned_date ? Math.floor((new Date() - new Date(patient.assigned_date)) / (1000 * 60 * 60 * 24)) : '-'}
                </div>
                <div className="text-sm text-gray-600">Dagar sedan anslutning</div>
              </div>
            </div>
          </div>

          {/* Important Messages Section */}
          {loading && (
            <div className="mt-8 bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Viktiga meddelanden</h3>
              <div className="flex items-center justify-center py-8">
                <Icon name="loader" size={24} className="text-blue-500 animate-spin" />
                <span className="ml-3 text-sm text-gray-600">Laddar viktiga meddelanden...</span>
              </div>
            </div>
          )}

          {!loading && importantMessages.length > 0 && (
            <div className="mt-8 bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Viktiga meddelanden</h3>
              <div className="space-y-3">
                {importantMessages.slice(0, 10).map((message, index) => {
                  const config = getMessageTypeConfig(message.message_type, message.importance_score);
                  if (!config) return null;

                  const isExpanded = expandedMessages[message.id];
                  const shouldShowToggle = message.message?.length > 150;

                  return (
                    <div 
                      key={`${message.id}-${index}`}
                      className={`p-4 rounded-lg border ${config.bgColor}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-lg">{config.emoji}</span>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {formatMessageDateTime(message.timestamp)}
                            </div>
                            <div className={`text-sm font-medium ${config.color}`}>
                              {config.label}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          {message.importance_score && (
                            <span className="text-xs text-gray-500">
                              {message.importance_score}⭐
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 text-sm text-gray-700">
                        {isExpanded ? message.message : message.message?.substring(0, 150)}
                        {shouldShowToggle && !isExpanded && '...'}
                      </div>
                      {shouldShowToggle && (
                        <button
                          onClick={() => toggleMessageExpanded(message.id)}
                          className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {isExpanded ? 'Visa mindre' : 'Visa mer...'}
                        </button>
                      )}
                    </div>
                  );
                })}
                
                {importantMessages.length > 10 && (
                  <div className="text-center pt-2">
                    <button
                      onClick={handleViewChat}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Visa alla viktiga meddelanden ({importantMessages.length})
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {!loading && importantMessages.length === 0 && stats?.total_messages > 0 && (
            <div className="mt-8 bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Viktiga meddelanden</h3>
              <div className="text-center py-8 text-gray-500">
                <Icon name="message-circle" size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-sm">Inga särskilt viktiga meddelanden att visa ännu.</p>
                <p className="text-xs mt-1">Meddelanden klassificeras automatiskt baserat på innehåll.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default PatientDetails;
